import { RUN_CONTRACTS } from '../utils/constants';

export type RunContractKind =
  | 'escape'
  | 'takedown'
  | 'chaos'
  | 'breaker'
  | 'style'
  | 'rush';

export type RunModifierKind =
  | 'hot_grid'
  | 'short_fuse'
  | 'clean_delivery'
  | 'style_sponsor'
  | 'breaker_bonus'
  | 'chaos_permit';

export type RunModifier = {
  kind: RunModifierKind;
  label: string;
  description: string;
};

export type RunContractStats = {
  takedowns: number;
  propertyDamage: number;
  pursuitBreakers: number;
  stylePoints: number;
  elapsedS: number;
};

export type RunContract = {
  kind: RunContractKind;
  title: string;
  intro: string;
  completeLine: string;
  briefing: string;
  handler: string;
  difficulty: 'Standard' | 'Hard' | 'Extreme';
  rewardTag: string;
  modifiers: RunModifier[];
};

export const RUN_CONTRACT_STORAGE_KEY = 'lm-selected-contract';
export const RUN_SEED_STORAGE_KEY = 'lm-selected-run-seed';
export const RUN_MODE_STORAGE_KEY = 'lm-selected-run-mode';

const CONTRACTS: RunContract[] = [
  {
    kind: 'escape',
    title: 'LAST MILE',
    intro: 'Reach extraction alive.',
    completeLine: 'Extraction route open.',
    briefing: 'Drive the package through the lockdown corridor. Stay clean, stay fast, get out.',
    handler: 'Dispatch',
    difficulty: 'Standard',
    rewardTag: 'Balanced XP',
    modifiers: [
      {
        kind: 'clean_delivery',
        label: 'Clean Delivery',
        description: 'Cleaner driving earns better grades and XP.',
      },
    ],
  },
  {
    kind: 'takedown',
    title: 'TAKEDOWN CONTRACT',
    intro: `Disable ${RUN_CONTRACTS.TAKEDOWN_TARGET} police units, then extract.`,
    completeLine: 'Takedown quota met.',
    briefing: 'They boxed our crew. Break their pursuit line before you run for extraction.',
    handler: 'Crew Chief',
    difficulty: 'Hard',
    rewardTag: 'Takedown XP',
    modifiers: [
      {
        kind: 'hot_grid',
        label: 'Hot Grid',
        description: 'Police pressure starts earlier.',
      },
    ],
  },
  {
    kind: 'chaos',
    title: 'CHAOS RUN',
    intro: `Cause $${RUN_CONTRACTS.CHAOS_BILL_TARGET.toLocaleString()} chaos, then extract.`,
    completeLine: 'Chaos quota met.',
    briefing: 'Make the city cameras look the wrong way. Property damage opens the route.',
    handler: 'Ghost Relay',
    difficulty: 'Hard',
    rewardTag: 'Chaos XP',
    modifiers: [
      {
        kind: 'chaos_permit',
        label: 'Chaos Permit',
        description: 'Damage is the job. Make the city loud.',
      },
      {
        kind: 'hot_grid',
        label: 'Hot Grid',
        description: 'Police pressure starts earlier.',
      },
    ],
  },
  {
    kind: 'breaker',
    title: 'PURSUIT BREAKER ROUTE',
    intro: `Trigger ${RUN_CONTRACTS.PURSUIT_BREAKER_TARGET} pursuit breaker before extraction.`,
    completeLine: 'Breaker route cleared.',
    briefing: 'Use the city against them. One breaker hit clears the escape channel.',
    handler: 'Route Control',
    difficulty: 'Standard',
    rewardTag: 'Breaker bonus',
    modifiers: [
      {
        kind: 'breaker_bonus',
        label: 'Breaker Bonus',
        description: 'Pursuit breakers heavily boost run quality.',
      },
    ],
  },
  {
    kind: 'style',
    title: 'STYLE CHAIN',
    intro: `Bank ${RUN_CONTRACTS.STYLE_POINTS_TARGET.toLocaleString()} style points before extraction.`,
    completeLine: 'Style quota met.',
    briefing: 'The sponsors are watching. Chain drift, near-miss, and takedown style.',
    handler: 'Blackbox',
    difficulty: 'Extreme',
    rewardTag: 'Style XP',
    modifiers: [
      {
        kind: 'style_sponsor',
        label: 'Style Sponsor',
        description: 'Style chains matter more than raw escape time.',
      },
      {
        kind: 'hot_grid',
        label: 'Hot Grid',
        description: 'Police pressure starts earlier.',
      },
    ],
  },
  {
    kind: 'rush',
    title: 'RUSH DELIVERY',
    intro: `Extract under ${RUN_CONTRACTS.FAST_ESCAPE_TARGET_S}s for the bonus.`,
    completeLine: 'Fast route still alive.',
    briefing: 'Short window, long odds. Hit the route hard before police lock the grid.',
    handler: 'Clockwatch',
    difficulty: 'Extreme',
    rewardTag: 'Speed bonus',
    modifiers: [
      {
        kind: 'short_fuse',
        label: 'Short Fuse',
        description: 'The run timer is tighter.',
      },
      {
        kind: 'hot_grid',
        label: 'Hot Grid',
        description: 'Police pressure starts earlier.',
      },
    ],
  },
];

export function allRunContracts(): readonly RunContract[] {
  return CONTRACTS;
}

export function runContractByKind(kind: string | null | undefined): RunContract | null {
  if (!kind) return null;
  return CONTRACTS.find((c) => c.kind === kind) ?? null;
}

export function pickRunContract(seed: number, multiplayer: boolean): RunContract {
  if (multiplayer) return CONTRACTS[0]!;
  const index = Math.abs(seed) % CONTRACTS.length;
  return CONTRACTS[index]!;
}

export function isRunContractComplete(contract: RunContract, stats: RunContractStats): boolean {
  switch (contract.kind) {
    case 'takedown':
      return stats.takedowns >= RUN_CONTRACTS.TAKEDOWN_TARGET;
    case 'chaos':
      return stats.propertyDamage >= RUN_CONTRACTS.CHAOS_BILL_TARGET;
    case 'breaker':
      return stats.pursuitBreakers >= RUN_CONTRACTS.PURSUIT_BREAKER_TARGET;
    case 'style':
      return stats.stylePoints >= RUN_CONTRACTS.STYLE_POINTS_TARGET;
    case 'rush':
      return true;
    case 'escape':
    default:
      return true;
  }
}

export function runContractProgress(contract: RunContract, stats: RunContractStats): string {
  switch (contract.kind) {
    case 'takedown':
      return `${Math.min(stats.takedowns, RUN_CONTRACTS.TAKEDOWN_TARGET)}/${RUN_CONTRACTS.TAKEDOWN_TARGET} takedowns`;
    case 'chaos':
      return `$${Math.min(stats.propertyDamage, RUN_CONTRACTS.CHAOS_BILL_TARGET).toLocaleString()}/$${RUN_CONTRACTS.CHAOS_BILL_TARGET.toLocaleString()} chaos`;
    case 'breaker':
      return `${Math.min(stats.pursuitBreakers, RUN_CONTRACTS.PURSUIT_BREAKER_TARGET)}/${RUN_CONTRACTS.PURSUIT_BREAKER_TARGET} breakers`;
    case 'style':
      return `${Math.min(Math.floor(stats.stylePoints), RUN_CONTRACTS.STYLE_POINTS_TARGET).toLocaleString()}/${RUN_CONTRACTS.STYLE_POINTS_TARGET.toLocaleString()} style`;
    case 'rush':
      return stats.elapsedS <= RUN_CONTRACTS.FAST_ESCAPE_TARGET_S
        ? `${Math.max(0, RUN_CONTRACTS.FAST_ESCAPE_TARGET_S - stats.elapsedS).toFixed(1)}s bonus window`
        : 'Bonus missed - extract anyway';
    case 'escape':
    default:
      return 'Extraction route open';
  }
}

export function runContractBlockLine(contract: RunContract, stats: RunContractStats): string {
  if (contract.kind === 'rush') return 'Rush bonus missed, but extraction is open.';
  return `${runContractProgress(contract, stats)} required`;
}

export function runContractBriefingLine(contract: RunContract): string {
  return `${contract.handler}: ${contract.briefing}`;
}

export function runModifierLine(contract: RunContract): string {
  return contract.modifiers.map((m) => m.label).join(' + ');
}
